import React, {useState, useContext} from 'react';
import {
  IconButton,
  List,
  ListItem,
  Box,
  Drawer,
  useMediaQuery,
  Button,
  Typography,
  ListItemIcon,
  ListItemButton,
} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import StyledListItemButton from '../buttons/StyledListItemButton';
import SocialMediaBottomNavigation from './socialMediaNavigation';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTipOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useLocation} from 'react-router-dom';
import {faCreditCard, faEllipsisStroke, faChartLine, faUser} from '@fortawesome/pro-light-svg-icons';
import {faBars, faCheck, faCoins} from '@fortawesome/free-solid-svg-icons';
import InformativeDialog from '../dialogbox/informativeDialog';
import {useActivityClickApi} from '../../api/activityClick';
import MiscContext from '../../context/MiscContext';
import {useCheckoutPaymentsApi} from '../../api/payment';
import {mainLinearGradient} from '../../theme/gradients';
import {paymentsEnabled} from '../../constants';
import {PricingPlansPopup} from '../pricingOptions/pricingPlansPopup';
import { faCoinVertical } from '@fortawesome/pro-regular-svg-icons';

/**
 * Represents an array of sidebar options for the SideBarMenu component that is below
 * Each object in the array should have the following properties.
 *
 * @property {string} primaryText - Title of this option
 * @property {string} secondaryText - Subtitle of this option
 * @property {string} routeTo - Valid react-router-dom route to go to(might include whole website URLs in the future)
 * @property {JSX.Element} icon - Icon component that will be shown with the option, right now we are using FontAwesomeIcon components
 */
const sideBarOpts = [
  // options that might be needed in the future
  {
    primaryText: 'Profile',
    secondaryText: 'Your name, email, and phone number information.',
    icon: <FontAwesomeIcon icon={faUser} size="xl" style={{color: '#A1A0A0'}} />,
    routeTo: '/profile',
  },
  {
    primaryText: 'Subscription and Payments',
    secondaryText: 'Edit your card information and change your subscription.',
    routeTo: '/pricing',
    icon: <FontAwesomeIcon icon={faCreditCard} size="xl" style={{color: '#A1A0A0'}} />,
  },
  {
    primaryText: 'Dashboard',
    secondaryText: 'Check your vitals and symptom checker history.',
    routeTo: '/dashboard',
    icon: <FontAwesomeIcon icon={faChartLine} size="xl" style={{color: '#A1A0A0'}} />,
  },
  {
    primaryText: 'Terms of Service',
    secondaryText: 'Read our terms of service.',
    routeTo: '/terms-of-service',
    icon: <FontAwesomeIcon icon={faEllipsisStroke} size="xl" style={{color: '#A1A0A0'}} />,
  },
  {
    primaryText: 'Privacy Policy',
    secondaryText: 'Read our privacy policy.',
    routeTo: '/privacy-policy',
    icon: <PrivacyTipIcon fontSize="medium" sx={{color: '#A1A0A0'}} />,
  },
  {
    primaryText: 'Credits',
    secondaryText: 0,
    routeTo: '/profile',
    icon: <FontAwesomeIcon icon={faCoinVertical} size="2x" style={{color: 'gold'}}  />
  }
];

/**
 * TriggerButton Component
 *
 * Button with all set up to display a drawer(no need to use external state, this handles all)
 * @param {Object} props - component props
 * @param {Array<func|object|bool>|func|object} props.iconButtonStyles - sx prop passed to the button used in this component
 *
 */
export const TriggerButton = ({iconButtonStyles}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton
        edge="end"
        size="large"
        color="inherit"
        aria-label="menu"
        sx={iconButtonStyles}
        onClick={() => setOpen(true)}
      >
        <FontAwesomeIcon icon={faBars} color="black" />
      </IconButton>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <SideBarMenu setIsMenuOpen={setOpen} />
      </Drawer>
    </>
  );
};

const SideBarMenu = ({setIsMenuOpen}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [paymentsPopupOpen, setPaymentsPopupOpen] = useState(false);
  const [informativeDialog, setInformativeDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
    width: 'sm',
  });
  const location = useLocation();
  const paymentsApi = useCheckoutPaymentsApi();
  const activityClickApi = useActivityClickApi();
  const {userPlan,userCredits} = useContext(MiscContext);
  const navigate = useNavigate();
  
  const handleListItemClick = async (_, index, title) => {
    if (title === 'Subscription and Payments' && userPlan === 'premium') {
      await paymentsApi.getPortal().then(res => {
        window.location.href = res.data.redirect_url;
      });
      return;
    } else if (title === 'Dashboard' && userPlan === 'premium') {
      navigate('/dashboard');
      return;
    }

    //open modal popup
    if (
      (title === 'Subscription and Payments' && userPlan !== 'premium') ||
      (title === 'Dashboard' && userPlan !== 'premium')
    ) {
      setInformativeDialog({
        title: 'Coming soon',
        subTitle: `${
          title === 'Subscription and Payments'
            ? 'Paid version '
            : `${title} is a premium feature. Our premium version `
        } has not been released, we'll update you when it is!`,
        isOpen: true,
        width: 'sm',
        onConfirm: () => {
          let buttonName = '';
          if (title === 'Subscription and Payments') {
            buttonName = 'payments_option';
          } else if (title === 'Dashboard') {
            buttonName = 'dashboard_option';
          }

          //Save in DB click activity of corresponding button
          activityClickApi.postActivityClick(buttonName);

          setInformativeDialog(prev => {
            return {...prev, isOpen: false};
          });
        },
      });
    }

    setSelectedIndex(index);
  };

  const handleDrawerClose = () => {
    setIsMenuOpen(false);
  };

  const handleUpgradeClick = async () => {
    //Save in DB click activity of corresponding button
    activityClickApi.postActivityClick('premium_button');

    if (!paymentsEnabled) {
      //open modal popup (this for first released which does not include premium version)
      setInformativeDialog({
        title: 'Coming soon',
        subTitle: `Our premium version has not been released, we'll update you when it is!`,
        isOpen: true,
        width: 'sm',
        onConfirm: () => {
          setInformativeDialog(prev => {
            return {...prev, isOpen: false};
          });
        },
      });
      return;
    }
    setPaymentsPopupOpen(true);
  };

  const isSmallScreen = useMediaQuery('(max-width:600px)');

  return (
    <>
      {isSmallScreen && (
        <Button
          aria-label="back"
          startIcon={<ArrowBackIcon />}
          component="label"
          sx={{
            color: '#3DA9F8',
            fontWeight: 500,
            fontSize: 14,
            justifyContent: 'flex-start',
            mt: 2,
            ml: 3,
          }}
          onClick={handleDrawerClose}
        >
          Back
        </Button>
      )}
      <List sx={{maxWidth: 400}}>
        <ListItem sx={{p: 3}}>
          {userPlan === 'premium' ? (
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '16px',
                fontWeight: '700',
                mr: 1,
                background: 'linear-gradient(91.02deg, #31A6EE -6.18%, #35C6C9 43.59%, #A6E177 94.47%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              <ListItemIcon sx={{justifyContent: 'right', mr: 1}}>
                <FontAwesomeIcon icon={faCheck} size="sm" style={{color: '#3DA9F8'}} />
              </ListItemIcon>
              Upgraded to Premium
            </Typography>
          ) : userPlan === 'basic' ? (
            <Button
              variant="contained"
              aria-label="upgradePremium"
              component="label"
              sx={{
                background: `linear-gradient(91.76deg, #31A6EE 0.45%, #35C6C9 53.85%, #A6E177 100%)`,
                borderColor: mainLinearGradient,
                color: 'white',
                borderRadius: 5,
                fontWeight: 600,
                fontSize: 14,
                ml: 7,
                mr: 3,
                borderWidth: 3,
              }}
              onClick={handleUpgradeClick}
            >
              Upgrade to Premium
            </Button>
          ) : null}
        </ListItem>
        {sideBarOpts.map((option, idx) => {
          if (option.routeTo === location.pathname) return;
          if (option.primaryText === 'Subscription and Payments' && userPlan === 'basic') return;
          return (
            <ListItem
              button
              key={idx}
              selected={selectedIndex === idx}
              onClick={e => handleListItemClick(e, idx, option.primaryText)}
              sx={{
                marginBottom: { xs :  (sideBarOpts.length - 1) === idx ? "70px" : "0px"}
              }} // add marginBottom as sharing button overlapping last item 
       
            >
              <StyledListItemButton
                component={Link}
                // if the options directs to dashboard or pricing, the page won't redirect anywhere
                to={
                  option.routeTo === '/pricing' || (option.routeTo === '/dashboard' && userPlan !== 'premium')
                    ? location.pathname
                    : option.routeTo
                }
                selected={selectedIndex === idx}
                onClick={event => handleListItemClick(event, idx, option.primaryText)}
                itemPrimaryText={option.primaryText}
                itemSecondaryText={option.primaryText === "Credits" && userCredits ? Number (userCredits) : option.secondaryText }
                itemIcon={option.icon}
              />
            </ListItem>
          );
        })}
        {isSmallScreen && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              pl: 12,
              py: 1,
            }}
          >
            {userPlan === 'basic' ? (
              <Button
                variant="contained"
                aria-label="upgradePremium"
                component="label"
                sx={{
                  background: `linear-gradient(91.76deg, #31A6EE 0.45%, #35C6C9 53.85%, #A6E177 100%)`,
                  borderColor: mainLinearGradient,
                  color: 'white',
                  borderRadius: 5,
                  fontWeight: 600,
                  fontSize: 14,
                  mr: 3,
                  borderWidth: 3,
                }}
                onClick={handleUpgradeClick}
              >
                Upgrade to Premium
              </Button>
            ) : null}
          </Box>
        )}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            px: isSmallScreen ? 3 : 5,
            py: 1,
            borderTop: '1px solid #ccc',
            backgroundColor : "white"
          }}
        >
          <SocialMediaBottomNavigation />
        </Box>
      </List>
      <PricingPlansPopup open={paymentsPopupOpen} setOpen={setPaymentsPopupOpen} />
      {/* Modal dialog box when user clicks dashboard or subscriptions options in sidebar menu */}
      <InformativeDialog informativeDialog={informativeDialog} setInformativeDialog={setInformativeDialog} />
    </>
  );
};

export default SideBarMenu;
